import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Field, ImageField, LinkField, Text } from '@sitecore-jss/sitecore-jss-nextjs';
import { ComponentProps } from 'lib/component-props';
import { NearbySiteSummary } from 'components/SiteMaster/types';
import { getSiteDetailUrlFromCoveo } from 'components/SiteMaster/utils';
import Carousel from 'components/shared/CarouselV2/Carousel';
import UiContainer from 'components/shared/Container/Container';
import useSavedSitesResultList from 'lib/hooks/coveo/sitesHeadless/useSavedSitesResultList';
import { SiteSearchResultRaw, getSiteIdFromCoveoRaw } from 'lib/types/coveo/SiteSearchResult';
import savedSitesHeadlessEngine from 'lib/utils/coveo/sitesHeadless/savedSitesHeadlessEngine';
import { getSavedSiteIds, withUpdatedSavedSiteIds } from 'lib/utils/saveSite';
import { saveSiteToProfile } from '../../services/RegisterUserService';
import { setProfileData } from '../../features/crProfileSlice';
import { hideLoader, showLoader } from '../../features/loaderSlice';
import { RootState } from '../../store/store';
import { useNearBySiteSearchLocation } from 'components/NearBySiteLocation/useNearBySiteSearchLocation';
import SavedSiteCard from './SavedSiteCard';
import {
  StSavedSiteCarouselSection,
  StSavedSiteCarouselTitle,
  StSavedSiteCarouselCarouselWrap,
} from './SavedSiteCarousel.styles';

interface SavedSiteCarouselFields {
  title?: Field<string>;
  mapPin?: ImageField;
  viewDetailsButton?: Field<string>;
  favoriteButtonColor?: Field<string>;
  favoriteButtonToolTipBGColor?: Field<string>;
  unSaveButtonHoverTextLoggedIn?: Field<string>;
  viewAllButton?: LinkField;
}

type SavedSiteCarouselProps = ComponentProps & {
  fields?: SavedSiteCarouselFields;
};

export const mapCoveoResultToSavedSite = (raw: SiteSearchResultRaw): NearbySiteSummary => ({
  id: getSiteIdFromCoveoRaw(raw),
  name: raw.site_name,
  url: getSiteDetailUrlFromCoveo(raw),
  city: raw.site_city,
  state: raw.site_statecode || raw.site_state,
  distanceMiles: raw.distance ?? 0,
  conditions: raw.site_relatedconditions ?? [],
});

function SavedSiteCarousel(props: SavedSiteCarouselProps): JSX.Element {
  const fields = props?.fields;
  const dispatch = useDispatch();
  const profileData = useSelector((state: RootState) => state.crProfile?.profileData);
  const savedSiteIds = getSavedSiteIds(profileData);
  const { latitude, longitude, isLocationReady } = useNearBySiteSearchLocation(true);
  const [unsavingId, setUnsavingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const hasCoords = isLocationReady && latitude != null && longitude != null;

  const { state } = useSavedSitesResultList(savedSitesHeadlessEngine, {
    latitude: hasCoords ? latitude : undefined,
    longitude: hasCoords ? longitude : undefined,
  });

  const savedSites = useMemo(() => {
    const results = state?.results ?? [];
    //Card order must match profile preferred site IDs
    const byId = new Map(
      results
        .map((result) => mapCoveoResultToSavedSite(result.raw as unknown as SiteSearchResultRaw))
        .filter((site) => Boolean(site.id))
        .map((site) => [String(site.id).trim(), site] as const)
    );
    return savedSiteIds
      .map((id) => byId.get(String(id).trim()))
      .filter((site): site is NearbySiteSummary => Boolean(site));
  }, [state?.results, savedSiteIds]);

  const handleUnsave = useCallback(
    async (site: NearbySiteSummary) => {
      if (!site.id || unsavingId) {
        return;
      }
      setUnsavingId(site.id);
      try {
        dispatch(showLoader());
        await saveSiteToProfile(site.id, false, site.name);
        dispatch(setProfileData(withUpdatedSavedSiteIds(profileData, site.id, false)));
      } finally {
        dispatch(hideLoader());
        setUnsavingId(null);
      }
    },
    [dispatch, profileData, unsavingId]
  );

  if (!fields || savedSiteIds.length === 0 || savedSites.length === 0) {
    return <></>;
  }

  return (
    <StSavedSiteCarouselSection id="saved-site-carousel-section">
      <UiContainer>
        <StSavedSiteCarouselTitle id="saved-site-carousel-title">
          <Text field={fields.title} />
        </StSavedSiteCarouselTitle>

        <StSavedSiteCarouselCarouselWrap id="saved-site-carousel-carousel-wrapper">
          <Carousel columnSize={3} spacing={2} showScrollbar>
            {savedSites.map((site) => (
              <Carousel.Item key={site.id} columnSize={3}>
                <SavedSiteCard
                  site={site}
                  fields={fields}
                  onUnsave={handleUnsave}
                  isUnsaving={unsavingId === site.id}
                  expanded={expandedId === site.id}
                  onToggleExpanded={() =>
                    setExpandedId((prev) => (prev === site.id ? null : site.id))
                  }
                />
              </Carousel.Item>
            ))}
          </Carousel>
        </StSavedSiteCarouselCarouselWrap>
      </UiContainer>
    </StSavedSiteCarouselSection>
  );
}

export default SavedSiteCarousel;
